import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';

import ProsperCardSvg from '../../assets/card-isometric.svg';
import UpdateSuccessSvg from '../../assets/icons/file-check-shiny-round.svg';
import KiteInSkySvg from '../../assets/icons/kite-in-sky.svg';
import LockIconSvg from '../../assets/icons/secure-encryption-lock.svg';
import {
  ActionAnchor,
  InlineButton,
  PrimaryButton,
} from 'src/commons/components/Button';
import Card, {
  ErrorCard,
  InputUpdateCard,
  LoadingCard,
} from 'src/commons/components/Card';
import { CurrencyTextInput } from 'src/commons/components/CurrencyTextInput';
import { Footer } from 'src/commons/components/Footer';
import Heading from 'src/commons/components/Heading';
import {
  MessageBubbleActionItem,
  MessageBubbleHeaderText,
} from 'src/commons/components/MessageBubble';
import { ModalWrapper } from 'src/commons/components/ModalWrapper';
import { Nav } from 'src/commons/components/Nav/Nav';
import { Page } from 'src/commons/components/Page';
import SubHeading from 'src/commons/components/SubHeading';
import {
  colors,
  MOBILE_BREAKPOINT,
  PREPOP_UPDATE_INCOME_KEY,
  TABLET_BREAKPOINT,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import {
  cardStarUpdateCreditCardIncomeCollection,
  cardStarValidateCreditCardIncomeCollectionOutbound,
} from 'src/commons/data/queries';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import {
  finalizeCurrency,
  formatCurrencyInputWithCommas,
  MAX_REGEX,
} from 'src/commons/utilities/formatting';
import {
  heapTrackAnnualIncomeUpdated,
  logError,
} from 'src/commons/utilities/logging';
import { goToDashaboard } from 'src/commons/utilities/routes';

interface AnnualIncomeValues {
  annualIncome: string;
}

// #region -- Styles ------------------------------------------------------------------------------
const Body = styled.div`
  width: 100%;
  margin: 1.4375rem 0 0;
  display: flex;
  flex-flow: row-reverse nowrap;
  flex: 1;
  justify-content: center;
  align-items: flex-start;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    flex-flow: column;
    align-items: center;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0;
  }
`;
Body.displayName = 'Body';

const ProsperCard = styled(ProsperCardSvg)`
  width: 8rem;
`;
ProsperCard.displayName = 'ProsperCard';

const LockIcon = styled(LockIconSvg)`
  height: 0.7275rem;
`;
LockIcon.displayName = 'LockIcon';

const ConfirmContent = styled.div`
  max-width: 23.125rem;
  display: flex;
  flex-flow: column;
  flex: 1;
  justify-content: center;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0.625rem;
  }
`;
ConfirmContent.displayName = 'ConfirmContent';

const ConfirmSubHeading = styled(SubHeading)`
  margin: 1.125rem 3.125rem 0;
  line-height: 1.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.25rem 2rem 0;
  }
`;
ConfirmSubHeading.displayName = 'ConfirmSubHeading';

const ConfirmUpdatedValue = styled.p`
  margin: 0.75rem auto 0;
  font-size: 2.1875rem;
  line-height: 2.4375rem;
  color: ${colors.TEXT_ANCHOR};
`;
ConfirmUpdatedValue.displayName = 'ConfirmUpdatedValue';

const SubmitButton = styled(PrimaryButton)`
  width: 19.75rem;
  margin: 1.8125rem 1.6875rem 1.5625rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    margin: 1.8125rem auto 1.5625rem;
  }
`;
SubmitButton.displayName = 'SubmitButton';

const EditButton = styled(InlineButton)`
  margin: 0 auto 1rem;
  text-decoration: none;
  color: ${colors.TEXT_ANCHOR};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 auto 1.75rem;
  }
`;
EditButton.displayName = 'EditButton';

const LinkButton = styled(ActionAnchor)`
  width: 100%;
  max-width: 19.75rem;
  padding: 1rem 0;
`;
LinkButton.displayName = 'LinkButton';

const UpdateCard = styled(Card)`
  min-height: 27.25rem;
  margin: 1.4375rem auto 0.625rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 26.8125rem;
    margin: 0 0 1.625rem;
  }
`;
UpdateCard.displayName = 'UpdateCard';

const UpdateContent = styled.div`
  width: 100%;
  margin: 2.5rem auto 5rem;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.5rem 0 5.5625rem;
  }
`;
UpdateContent.displayName = 'UpdateContent';

const UpdateHeading = styled(Heading)`
  width: 17.75rem;
  margin: 1.8125rem auto 0;
  line-height: 2rem;
`;
UpdateHeading.displayName = 'UpdateHeading';

const UpdateSubHeading = styled(SubHeading)`
  margin: 1.5rem 0 1.8125rem;
  line-height: 1.5rem;
`;
UpdateSubHeading.displayName = 'UpdateSubHeading';

const ExpiredCard = styled(Card)`
  min-height: 25.375rem;
  margin: 1.4375rem auto 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 24.9375rem;
    margin: 0 0 1.625rem;
    padding: 0 1.84375rem;
  }
`;
ExpiredCard.displayName = 'ExpiredCard';

const ExpiredContent = styled(UpdateContent)`
  width: 19.75rem;
  margin: 3.125rem auto 5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    margin: 2.625rem auto 5rem;
  }
`;
ExpiredContent.displayName = 'ExpiredContent';

const ExpiredHeading = styled(Heading)`
  width: 100%;
  margin: 1.5rem auto 0;
  line-height: 2rem;
`;
ExpiredHeading.displayName = 'ExpiredHeading';
// #endregion

// List of criteria displayed in the message bubble under the input
const AnnualIncomeHintContents = () => (
  <>
    <MessageBubbleHeaderText>Include your:</MessageBubbleHeaderText>
    <MessageBubbleActionItem
      isPositiveAction
      actionText="Yearly earnings, before taxes"
    />
    <MessageBubbleActionItem
      isPositiveAction
      actionText="Bonuses, commissions, and variable pay"
    />
    <MessageBubbleActionItem
      isPositiveAction
      actionText="Retirement, disability, pension, or public assistance income"
    />
    <MessageBubbleActionItem isPositiveAction actionText="Rental income" />
    <MessageBubbleActionItem
      isPositiveAction
      actionText="Alimony, child support, or separate maintenance income are optional"
    />
    <MessageBubbleHeaderText>Don’t include:</MessageBubbleHeaderText>
    <MessageBubbleActionItem
      isPositiveAction={false}
      actionText="Income from other members of your household"
    />
  </>
);

// Confirmation modal that displays on "Update income" button click
const ConfirmUpdateModal = (
  showConfirmationModal: boolean,
  setShowConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>,
  handleSubmit: { (): void },
  updatedIncomeValue: string,
  incomeInputRef?: React.MutableRefObject<any>
) => {
  const handleClickEdit = () => {
    if (incomeInputRef.current) incomeInputRef.current.focus();
    setShowConfirmationModal(false);
  };

  return (
    <ModalWrapper
      id="annualIncome-update-confirm-modal"
      showModal={showConfirmationModal}
      setShowModal={setShowConfirmationModal}
    >
      <ConfirmContent>
        <UpdateHeading>Confirm income</UpdateHeading>
        <ConfirmUpdatedValue>{`$${updatedIncomeValue}`}</ConfirmUpdatedValue>
        <ConfirmSubHeading>
          We’ll ask you to update your income again in a year.
        </ConfirmSubHeading>
        <SubmitButton
          id="annualIncome-update-confirm-submit-button"
          type="submit"
          onClick={() => handleSubmit()}
        >
          Submit
        </SubmitButton>
        <EditButton
          id="annualIncome-update-confirm-edit-button"
          type="button"
          onClick={() => handleClickEdit()}
        >
          Edit income
        </EditButton>
      </ConfirmContent>
    </ModalWrapper>
  );
};

// Component --------------------------------------------------------------------------------------
export const UpdateAnnualIncome = () => {
  const [loading, setLoading] = useState(true);
  const [latestIncomeAmount, setLatestIncomeAmount] = useState('');
  const [alreadyUpdated, setAlreadyUpdated] = useState(false);
  const [expiredLink, setExpiredLink] = useState(false);
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showThankYouCard, setShowThankYouCard] = useState(false);

  const { incomeCollectionOutboundId } = useParams();
  const incomeInputRef = useRef(null);

  // Pre-pop the annualIncome field with latestIncomeAmount on page load if ff is enabled
  const enablePrepop = ifFeatureFlagOn(PREPOP_UPDATE_INCOME_KEY);

  // Validate income collection outbound id & record on page load
  useEffect(() => {
    if (incomeCollectionOutboundId) {
      validateIncomeRecord();
    }
  }, []);

  // Disables body scrolling while the confirmation modal is open
  useEffect(() => {
    if (showConfirmationModal) {
      disableBodyScroll(document.body);
    } else {
      enableBodyScroll(document.body);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [showConfirmationModal]);

  const formik = useFormik({
    initialValues: {
      annualIncome: latestIncomeAmount ?? '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      annualIncome: Yup.string()
        .trim()
        .required('Enter your annual pre-tax income')
        .max(9, 'Enter an annual income no greater than $9,999,999')
        .matches(
          MAX_REGEX,
          'Enter an annual income no greater than $9,999,999'
        ),
    }),
    onSubmit: async (values: AnnualIncomeValues) => {
      setLoading(true);
      try {
        const income = {
          annualIncome: finalizeCurrency(values.annualIncome.trim()),
        };
        const response = await cardStarUpdateCreditCardIncomeCollection(
          incomeCollectionOutboundId,
          income
        );
        const { errors } = response;
        if (errors) {
          const message =
            errors[0] && errors[0].code
              ? errors[0].code
              : 'Unexpected error occurred';
          throw new Error(message);
        }
        setShowThankYouCard(true); // Show "thank you" card on successful update
        heapTrackAnnualIncomeUpdated();
      } catch (error: any) {
        setShowErrorCard(true);
        if (error && error.message !== UNAUTHORIZED_MESSAGE) {
          logError(error, {
            errorName: error.name || 'PageError',
            errorMessage: error.message || 'Unexpected error occurred',
            component: 'UpdateAnnualIncome',
            function: 'onSubmit',
          });
        }
      } finally {
        setLoading(false);
        setShowConfirmationModal(false);
      }
    },
  });

  // Validate the update link & check if the user's annual income has already been updated
  async function validateIncomeRecord() {
    setLoading(true);
    try {
      const response = await cardStarValidateCreditCardIncomeCollectionOutbound(
        incomeCollectionOutboundId
      );
      const { data, errors } = response;
      if (errors) {
        const message =
          errors[0] && errors[0].code
            ? errors[0].code
            : 'Unexpected error occurred';
        throw new Error(message);
      }
      const {
        cardStarValidateCreditCardIncomeCollectionOutbound: {
          isActive,
          isIncomeUpdated,
          latestReportedIncome,
        },
      } = data;
      // Only set latestIncomeAmount if feature-flag is enabled & latestReportedIncome is defined
      if (enablePrepop && latestReportedIncome) {
        setLatestIncomeAmount(
          formatCurrencyInputWithCommas(latestReportedIncome.toString())
        );
      }
      if (isIncomeUpdated) setAlreadyUpdated(true);
      else if (!isActive) setExpiredLink(true);
    } catch (error: any) {
      if (error && error.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorCard(true);
        logError(error, {
          errorName: error.name || 'PageError',
          errorMessage: error.message || 'Unexpected error occurred',
          component: 'UpdateAnnualIncome',
          function: 'validateIncomeRecord',
        });
      }
    } finally {
      setLoading(false);
    }
  }

  // Displays confirmation modal on-click "Update income" button
  const handleClickUpdate = () => {
    const { values, errors, touched } = formik;
    if (!values.annualIncome || errors.annualIncome) {
      if (!touched.annualIncome) {
        // Show error on empty input value after bad submit attempt
        formik.setFieldTouched('annualIncome', true);
      }
      return;
    }
    setShowConfirmationModal(true);
  };

  // Render ---------------------------------------------------------------------------------------
  if (loading || formik.isSubmitting) {
    return (
      <>
        <Nav loading />
        <Page>
          <LoadingCard />
          <Footer onNonFunnelPage />
        </Page>
      </>
    );
  }

  if (showErrorCard) {
    return (
      <>
        <Nav />
        <Page>
          <ErrorCard />
          <Footer onNonFunnelPage />
        </Page>
      </>
    );
  }

  if (alreadyUpdated) {
    return (
      <>
        <Nav />
        <Page>
          <UpdateCard id="annualIncome-update-already-updated-card">
            <UpdateContent>
              <UpdateSuccessSvg aria-hidden />
              <UpdateHeading>
                We’ve already received your income update
              </UpdateHeading>
              <UpdateSubHeading>
                We’ll ask you again in a year.
              </UpdateSubHeading>
              <LinkButton
                id="annualIncome-update-already-updated-ch-link"
                href={goToDashaboard()}
                target="_blank"
              >
                Go to my Prosper® Card
              </LinkButton>
            </UpdateContent>
          </UpdateCard>
          <Footer onNonFunnelPage />
        </Page>
      </>
    );
  }
  if (expiredLink) {
    return (
      <>
        <Nav />
        <Page>
          <ExpiredCard id="annualIncome-update-expired-link-card">
            <ExpiredContent>
              <KiteInSkySvg aria-hidden />
              <ExpiredHeading>Sorry, this link has expired</ExpiredHeading>
              <UpdateSubHeading>
                We’ll reach out again when it’s time for your annual income
                update.
              </UpdateSubHeading>
              <LinkButton
                id="annualIncome-update-expired-link-ch-link"
                href={goToDashaboard()}
                target="_blank"
              >
                Go to my Prosper® Card
              </LinkButton>
            </ExpiredContent>
          </ExpiredCard>
          <Footer onNonFunnelPage />
        </Page>
      </>
    );
  }

  if (showThankYouCard) {
    return (
      <>
        <Nav />
        <Page>
          <UpdateCard id="annualIncome-update-thank-you-card">
            <UpdateContent>
              <UpdateSuccessSvg aria-hidden />
              <UpdateHeading>Thank you for updating your income</UpdateHeading>
              <UpdateSubHeading>
                We’ll ask you again in a year.
              </UpdateSubHeading>
              <LinkButton
                id="annualIncome-update-thank-you-ch-link"
                href={goToDashaboard()}
                target="_blank"
              >
                Go to my Prosper® Card
              </LinkButton>
            </UpdateContent>
          </UpdateCard>
          <Footer onNonFunnelPage />
        </Page>
      </>
    );
  }

  return (
    <>
      <Nav />
      <Page>
        <Body>
          {showConfirmationModal &&
            ConfirmUpdateModal(
              showConfirmationModal,
              setShowConfirmationModal,
              formik.handleSubmit,
              formik.values.annualIncome,
              incomeInputRef
            )}
          <InputUpdateCard
            heading="Update or confirm your income"
            fieldname="annualIncome"
            onUpdateSubmit={handleClickUpdate}
            iconBelowForm={<LockIcon aria-hidden />}
            infoBelowForm="&nbsp;256-bit encryption keeps your data safe."
            submitButtonText="Confirm income"
          >
            <CurrencyTextInput
              name="annualIncome"
              value={formik.values.annualIncome}
              label="$ Annual pre-tax income"
              error={formik.errors.annualIncome}
              touched={formik.touched.annualIncome}
              disabled={formik.isSubmitting}
              maxLength={9}
              prefixText=""
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              hintContents={<AnnualIncomeHintContents />}
              forwardRef={incomeInputRef}
              toggleHint
            />
          </InputUpdateCard>
        </Body>
        <Footer onNonFunnelPage />
      </Page>
    </>
  );
};
